/**
 * SUB-3.9 · FR53, FR55, FR56. Releasing a client account from its parent.
 *
 * Pure, and here rather than in the drawer, because the three things it decides
 * are the three things a superadmin has to be able to trust before clicking:
 *
 *   which accounts are this parent's children, and which of them are archived
 *   what the confirmation SAYS will happen, in the words of what will happen
 *   what the toast reports afterwards, from the count the database measured
 *
 * ⚠️ NOTHING HERE DECIDES WHETHER A DETACH IS ALLOWED. `detach_sub_account`
 * gates on `is_superadmin()` and reads nothing about the parent, which is FR55:
 * a client must never be strandable by an uncooperative or defunct provider. A
 * client-side precondition would be a second definition of that rule and the
 * only one the user would ever read. An archived parent, a parent with no active
 * Agency Admins and a parent at allowance zero are all detachable, and all three
 * are measured in joules-web `supabase/tests/verify_sub_3_9_detach.sql`.
 */

/** The fields of a tenant row this module needs. Structural, not the whole DTO. */
export interface SubAccountRow {
  tenant_id: string;
  company_name: string;
  parent_tenant_id?: string | null;
  is_archived?: boolean | null;
}

/** What `POST /tenants/{id}/detach` answers with. */
export interface DetachOutcome {
  status: string;
  former_parent_tenant_id?: string | null;
  access_revoked?: boolean;
  grants_revoked?: number;
}

/**
 * The children of one parent.
 *
 * Archived children are INCLUDED and flagged rather than filtered out, and that
 * is the same lesson `enforce_one_level_hierarchy` records in its own refusal
 * message: "has children" has two definitions in this epic. The allowance count
 * from `sub_account_usage()` excludes archived children (FR67), while rule 4
 * counts all of them, so a parent showing "2 of 5 sub-accounts" can still be
 * refused a disable because of a third child nobody can see. Hiding it here
 * would reproduce exactly that.
 *
 * Sorted by name, case-insensitively, so the list is stable between renders. The
 * database's own collation is not consulted because nothing here round-trips a
 * sort order; this is display only.
 */
export function selectSubAccounts<T extends SubAccountRow>(
  tenants: readonly T[],
  parentTenantId: string,
): T[] {
  if (!parentTenantId) return [];
  return tenants
    .filter((t) => t.parent_tenant_id === parentTenantId)
    .slice()
    .sort((a, b) =>
      a.company_name.localeCompare(b.company_name, undefined, {
        sensitivity: "base",
      }),
    );
}

/** "entry" / "entries", agreeing with the number in front of it. */
function entries(count: number): string {
  return count === 1 ? "entry" : "entries";
}

/**
 * FR56 in the confirmation, which is the one place a superadmin can still stop.
 *
 * It names the two things that DO change and states plainly that nothing else
 * does, because both halves are load-bearing: the detach is one column, and the
 * grants going away is the point of the action rather than a side effect of it.
 *
 * The number of grants is deliberately NOT predicted here. Reading it would take
 * a query whose answer could be stale by the time the button is pressed, and the
 * real figure comes back from the detach itself: see `describeDetachOutcome`.
 */
export function detachConfirmation(
  childName: string,
  parentName: string | null,
): { title: string; description: string; confirmLabel: string } {
  const parent = parentName?.trim() ? parentName : "the parent organization";
  return {
    title: `Release ${childName}?`,
    description:
      `${childName} becomes an ordinary top-level account. Its projects, ` +
      `documents, users, pipelines, checklists and settings are untouched, and ` +
      `it keeps the same account id. What ends is ${parent}'s access: every ` +
      `Admin entry that exists only because someone is an Agency Admin of ` +
      `${parent} is removed, and anyone from ${parent} currently working inside ` +
      `${childName} loses it immediately. ${parent} does not have to agree, and ` +
      `is not notified by this action.`,
    confirmLabel: "Release account",
  };
}

/**
 * The sentence after the fact, built from what the database counted.
 *
 * ⚠️ `grants_revoked` is a MEASUREMENT, not an estimate: `detach_sub_account`
 * reads `user_roles` either side of its own UPDATE, with the reconciling trigger
 * firing in between and no commit anywhere. Until 20260812n the payload carried a
 * hardcoded `access_revoked: false`, which had been the opposite of the truth
 * since the day the trigger shipped, so this wording exists to stop the UI
 * inventing the number instead.
 *
 * Zero is reported honestly and as a success. It is what a parent with no Agency
 * Admins looks like, and what a second click on an already-detached account looks
 * like.
 */
export function describeDetachOutcome(
  childName: string,
  outcome: DetachOutcome | null | undefined,
): string {
  const n = outcome?.grants_revoked ?? 0;
  if (n <= 0) {
    return `${childName} is now a standalone account. There were no parent-granted admin entries to remove.`;
  }
  return `${childName} is now a standalone account. ${n} parent-granted admin ${entries(
    n,
  )} removed.`;
}

/**
 * The label under a child's name in the list.
 *
 * Archived is stated in terms of the allowance, because that is the question the
 * number above it raises: FR67 excludes archived children from the count, so an
 * archived child in this list would otherwise look like a discrepancy.
 */
export function describeSubAccount(row: SubAccountRow): string {
  return row.is_archived
    ? "Archived. Does not count toward the allowance, but still blocks disabling sub-accounts."
    : "Active client account.";
}
